import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import scrollToElement from 'scroll-to-element';

import CustomVideo from 'components/shared/custom-video';
import SectionHeader from 'components/shared/landing/section-header';
import MainHeader, {
  HeaderText,
  Underline,
} from 'components/shared/landing/main-header';
import FormContact from 'components/form-contact';
import ScrollingValuePropSelector from 'components/shared/landing/scrolling-value-prop-selector';
import ValueProp from 'components/shared/landing/value-prop';
import IntegratingExplicitSELVideo from 'assets/videos/Unyted - Integrating Explicit SEL with Core Academics (short version).mp4';
import educatorPoster from 'assets/img/educator-video-poster.png';
import playIcon from 'assets/img/video-play-button.svg';
import SELIntegrationPDs from './images/SEL-Integration-PDs.png';
import UserManagement from './images/User-Management.png';
import OrganizeSmallGroups from './images/Organize-Small-Groups.png';
import UseActivityLibrary from './images/Use-Activity-Library.png';
import GetOptimalDiscourse from './images/Get-Optimal-Discourse.png';
import RealtimeDataReports from './images/Real-time-Data-Reports.png';
import styles from './styles.module.scss';

const Educator = () => (
  <div className={styles.educator}>
    <MainHeader
      header={
        <HeaderText className={styles.headerText}>
          <Underline>Learner-centered instruction</Underline> hits multiple
          goals at once
        </HeaderText>
      }
      subtext="Academic, behavioral and SEL gains all with one online tool that saves teachers time and eliminates teaching skills in isolation."
      onButtonClick={() => scrollToElement('#features')}
      buttonText="How It Works"
      buttonClassName="apple"
      videoElement={
        <CustomVideo
          src={IntegratingExplicitSELVideo}
          poster={educatorPoster}
          icon={playIcon}
        />
      }
      videoElementContainerClassName={styles.headerVideo}
      flip
    />
    <div id="features" className={styles.valuePropSelector}>
      <div className="container">
        <div className="row mb-5">
          <div id="scrolling-value-prop-selectors" className="col">
            <SectionHeader
              tagline="all-in-one platform"
              valueprop="Features Tailored for Educators"
              subtext="Built by educators, for educators who do more with less."
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-lg-6">
            <ScrollingValuePropSelector
              scrollTo="#sel-integration-pds"
              title="SEL Integration PDs"
              icon="chalkboard-teacher"
            />
          </div>
          <div className="col-12 col-lg-6">
            <ScrollingValuePropSelector
              scrollTo="#user-management"
              title="User Management"
              icon="address-card"
            />
          </div>
          <div className="col-12 col-lg-6">
            <ScrollingValuePropSelector
              scrollTo="#organize-small-groups"
              title="Organize Small Groups"
              icon="users"
            />
          </div>
          <div className="col-12 col-lg-6">
            <ScrollingValuePropSelector
              scrollTo="#use-activity-library"
              title="Use Activity Library"
              icon="books"
            />
          </div>
          <div className="col-12 col-lg-6">
            <ScrollingValuePropSelector
              scrollTo="#get-optimal-discourse"
              title="Optimal Learner Discourse"
              icon="bolt"
            />
          </div>
          <div className="col-12 col-lg-6">
            <ScrollingValuePropSelector
              scrollTo="#real-time-data-reports"
              title="Real-time Data Reports"
              icon="stars"
            />
          </div>
        </div>
      </div>
    </div>
    <div className={styles.pricing}>
      <div className={`container ${styles.box}`}>
        <div className="row p-4">
          <div className="col-12 col-md-6">
            <h2>Custom Pricing</h2>
            <p>Plans to fit your classroom, school, or district.</p>
            <div className={styles.ctaButton}>
              <Link to="/online-platform" className="button purple">
                See Pricing
              </Link>
            </div>
          </div>
          <div className="col-12 col-md-6">
            <h2>Unyted's Platform also includes:</h2>
            <ul className="fa-ul">
              <li>
                <span className="fa-li">
                  <FontAwesomeIcon icon="check" className="text-apple mr-3" />
                </span>
                Student SEL Profiles
              </li>
              <li>
                <span className="fa-li">
                  <FontAwesomeIcon icon="check" className="text-apple mr-3" />
                </span>
                SEL Explicit Lessons
              </li>
              <li>
                <span className="fa-li">
                  <FontAwesomeIcon icon="check" className="text-apple mr-3" />
                </span>
                Student Micro-Credentials
              </li>
              <li>
                <span className="fa-li">
                  <FontAwesomeIcon icon="check" className="text-apple mr-3" />
                </span>
                Ability to Create Own Activities
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <div className={styles.valueProps}>
      <div className="container">
        <div className="row mb-3">
          <div className="col">
            <SectionHeader
              tagline="proven results"
              valueprop="Fully Tested in Real Classrooms"
              subtext="7000+ students from diverse backgrounds and varying learning levels have benefited."
            />
          </div>
        </div>
        <div className="row">
          <ValueProp
            id="sel-integration-pds"
            image={SELIntegrationPDs}
            title="SEL Integration PDs"
            description={
              <span>
                Unyted's platform is fully supported by{' '}
                <Link to="/workshops">
                  in-person and online professional development led by deeply
                  experienced trainers.
                </Link>
              </span>
            }
            returnTo="#scrolling-value-prop-selectors"
            flipped
          />
        </div>
        <div className="row">
          <ValueProp
            id="user-management"
            image={UserManagement}
            title="User Management"
            description="Unyted's turn-key platform allows schools and districts to create communities, manage their users, and create classroom teams and small groups optimized for learning."
            returnTo="#scrolling-value-prop-selectors"
          />
        </div>
        <div className="row">
          <ValueProp
            id="organize-small-groups"
            image={OrganizeSmallGroups}
            title="Organize Small Groups"
            description="The first step in the magic is providing educators the ability to easily segment classrooms into groups and just as easily re-arrange to differentiate learning."
            returnTo="#scrolling-value-prop-selectors"
            flipped
          />
        </div>
        <div className="row">
          <ValueProp
            id="use-activity-library"
            image={UseActivityLibrary}
            title="Use Activity Library"
            description="Educators can save time by using Unyted's existing bank of standards-aligned activities or create their own activities to place in their community's Library."
            returnTo="#scrolling-value-prop-selectors"
          />
        </div>
        <div className="row">
          <ValueProp
            id="get-optimal-discourse"
            image={GetOptimalDiscourse}
            title="Optimal Learner Discourse"
            description={`We transformed the traditional discussion board so the magic of Unyted could center around what is gained from the "if you can explain it, you know it" philosophy.`}
            returnTo="#scrolling-value-prop-selectors"
            flipped
          />
        </div>
        <div className="row">
          <ValueProp
            id="real-time-data-reports"
            image={RealtimeDataReports}
            title="Real-time Data Reports"
            description={
              <span>
                Unyted is all about helping Educators maintain student-centered
                practices both academically and for SEL skills, so we provide{' '}
                <em>fully trackable, live data reports</em> so educators can
                accelerate gains.
              </span>
            }
            returnTo="#scrolling-value-prop-selectors"
          />
        </div>
      </div>
    </div>
    <div id="contact" className={styles.formContact}>
      <div className="container">
        <div className="row">
          <div className="col">
            <FormContact
              showHeader
              showSubtext
              messageLabel="School, Grade Level and Subject Matter"
              append="Type: Educator"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default Educator;
